import { env } from '../lib/env';
import { HERO_ASSETS } from '../lib/siteAssets';

const HERO_BANNER_CACHE_VERSION = '20260331';

export type BannerType = 'main' | 'insight' | 'announcement';

export interface HeroBanner {
  id: string;
  type: BannerType;
  enabled: boolean;
  order: number;
  // Shared
  backgroundImageUrl: string;
  overlayColor: string;
  // Main / Announcement
  badge?: string;
  /** Controls whether the badge pill is rendered on the slide. Default: true */
  showBadge?: boolean;
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  ctaPrimary?: { label: string; href: string };
  ctaSecondary?: { label: string; href: string };
  // Insight override
  insightMode: 'latest' | 'pinned';
  pinnedInsightId?: string;
  // Announcement extras
  announcementLabel?: string;
  announcementLink?: string;
  announcementLinkLabel?: string;
}

function currentBasePrefix() {
  const base = import.meta.env.BASE_URL || '/';
  if (base === '/') return '';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

function currentApiBase() {
  const apiBase = env.apiBaseUrl.trim();
  return apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
}

function withCurrentBase(path: string) {
  const normalizedPath = path.replace(/^\/+/, '');
  const basePrefix = currentBasePrefix();
  return basePrefix ? `${basePrefix}/${normalizedPath}` : `/${normalizedPath}`;
}

function buildCurrentPhotoUrl(storedName: string, cacheBust?: string) {
  const baseUrl = `${currentApiBase()}/photos.php?action=view&file=${encodeURIComponent(storedName)}`;
  return cacheBust ? `${baseUrl}&v=${encodeURIComponent(cacheBust)}` : baseUrl;
}

function appendCacheBust(url: string, cacheBust: string) {
  if (!cacheBust) return url;

  try {
    const parsed = new URL(url, 'https://zobu.local');
    parsed.searchParams.set('v', cacheBust);
    const normalized = `${parsed.pathname}${parsed.search}`;
    return /^https?:\/\//i.test(url) ? parsed.toString() : normalized;
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${encodeURIComponent(cacheBust)}`;
  }
}

function normalizeKnownHeroImageUrl(rawValue: string) {
  const value = rawValue.trim();
  if (!value) return '';

  if (/^(data:image\/|blob:)/i.test(value)) {
    return value;
  }

  const photoMatch = value.match(
    /^(?:https?:\/\/[^/]+)?\/?(?:staging\/)?api\/photos\.php\?action=view&file=([^&#]+)(?:&v=([^&#]+))?/i,
  );
  if (photoMatch?.[1]) {
    return buildCurrentPhotoUrl(
      decodeURIComponent(photoMatch[1]),
      photoMatch[2] ? decodeURIComponent(photoMatch[2]) : HERO_BANNER_CACHE_VERSION,
    );
  }

  const assetMatch = value.match(/^(?:https?:\/\/[^/]+)?\/?(?:staging\/)?(assets\/.+)$/i);
  if (assetMatch?.[1]) {
    const [assetPath, query = ''] = assetMatch[1].split('?');
    return appendCacheBust(withCurrentBase(assetPath) + (query ? `?${query}` : ''), HERO_BANNER_CACHE_VERSION);
  }

  if (/\/res\/default\/|readdy\.ai|storage\.readdy-site\.link/i.test(value)) {
    return '';
  }

  if (/^[a-zA-Z0-9._-]+\.(?:jpg|jpeg|png|gif|webp|svg)$/i.test(value)) {
    return buildCurrentPhotoUrl(value, HERO_BANNER_CACHE_VERSION);
  }

  return value;
}

export function getDefaultHeroBannerBackground(type: BannerType) {
  switch (type) {
    case 'insight':
      return HERO_ASSETS.bannerInsight;
    case 'announcement':
      return HERO_ASSETS.bannerExcellence;
    case 'main':
    default:
      return HERO_ASSETS.homeMain;
  }
}

export function resolveHeroBannerBackgroundUrl(type: BannerType, backgroundImageUrl?: string) {
  const normalized = normalizeKnownHeroImageUrl(backgroundImageUrl ?? '');
  return normalized || getDefaultHeroBannerBackground(type);
}

export function withHeroBannerCacheBust(type: BannerType, backgroundImageUrl: string, cacheBust: string) {
  const resolved = resolveHeroBannerBackgroundUrl(type, backgroundImageUrl);
  const fallback = getDefaultHeroBannerBackground(type);

  if (!resolved || resolved === fallback) {
    return fallback;
  }

  return appendCacheBust(resolved, cacheBust);
}

export function normalizeHeroBanner(banner: HeroBanner, index = 0): HeroBanner {
  return {
    ...banner,
    enabled: banner.enabled !== false,
    order: typeof banner.order === 'number' ? banner.order : index + 1,
    showBadge: banner.showBadge !== false,
    backgroundImageUrl: resolveHeroBannerBackgroundUrl(banner.type, banner.backgroundImageUrl),
  };
}

export function normalizeHeroBanners(banners: HeroBanner[]) {
  return banners
    .map((banner, index) => normalizeHeroBanner(banner, index))
    .sort((left, right) => left.order - right.order)
    .map((banner, index) => ({
      ...banner,
      order: index + 1,
    }));
}

export const mockHeroBanners: HeroBanner[] = [
  {
    id: 'banner-main',
    type: 'main',
    enabled: true,
    order: 1,
    backgroundImageUrl: HERO_ASSETS.homeMain,
    overlayColor: 'from-[#1c2937]/70 via-[#1c2937]/55 to-[#1c2937]/80',
    badge: 'Certified Management Consultants',
    showBadge: false,
    title: 'Expertise You Can',
    titleHighlight: 'Trust',
    subtitle: 'Management Consultancy, Policy Development and Engineering Consultancy — tailored solutions for complex challenges since 2004.',
    ctaPrimary: { label: 'Explore Our Services', href: '/services' },
    ctaSecondary: { label: 'Get in Touch', href: '/contact' },
    insightMode: 'latest',
  },
  {
    id: 'banner-insight',
    type: 'insight',
    enabled: true,
    order: 2,
    backgroundImageUrl: HERO_ASSETS.bannerInsight,
    overlayColor: 'from-[#1c2937]/80 via-[#1c2937]/65 to-[#1c2937]/85',
    insightMode: 'latest',
  },
  {
    id: 'banner-newyear',
    type: 'announcement',
    enabled: false,
    order: 3,
    backgroundImageUrl: HERO_ASSETS.bannerNewYear,
    overlayColor: 'from-[#0a1628]/75 via-[#0a1628]/55 to-[#0a1628]/80',
    badge: 'Happy New Year',
    showBadge: true,
    title: 'Wishing You a Year of',
    titleHighlight: 'Growth & Success',
    subtitle: 'From all of us at Zobu Consulting — may 2025 bring clarity, ambition and the right partnerships to help you thrive.',
    announcementLabel: 'New Year 2025',
    ctaPrimary: { label: 'Work With Us in 2025', href: '/contact' },
    ctaSecondary: { label: 'Our Services', href: '/services' },
    announcementLink: '/contact',
    announcementLinkLabel: 'Start a Conversation',
    insightMode: 'latest',
  },
  {
    id: 'banner-excellence',
    type: 'announcement',
    enabled: false,
    order: 4,
    backgroundImageUrl: HERO_ASSETS.bannerExcellence,
    overlayColor: 'from-[#1c2937]/75 via-[#1c2937]/60 to-[#1c2937]/85',
    badge: 'Since 2004',
    showBadge: true,
    title: 'Twenty Years of',
    titleHighlight: 'Strategic Excellence',
    subtitle: 'Two decades of delivering measurable results for public bodies, corporations and entrepreneurs across Turkey and beyond.',
    ctaPrimary: { label: 'Our Story', href: '/about' },
    ctaSecondary: { label: 'View Our Work', href: '/services' },
    announcementLabel: '20 Years',
    insightMode: 'latest',
  },
];
